import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  Flex,
  Input,
  IconButton,
  Text,
  Spinner,
  Textarea,
  useToast,
  useColorModeValue,
  useTheme
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";

function EditableText({
  initialValue,
  onSave, // onSave now expected to be a promise that resolves to a boolean
  placeholder,
  pattern,
  invalid,
  editable = true,
  multiline = false,
  showValidationToast = true,
  onCancel = async ()=>{},
  mode = "display",  //if it's "edit" it initializes in edit mode
  onModeChange = (_) => {},
  showPlaceholderInDisplayMode = true,
  truncate = false,
  // The text wraps only between words, and a word too long for the whole width is cut short
  // with an ellipsis; the edit button moves below a text it does not fit beside, rather than
  // the text breaking inside a word to make room for it. For a narrow, fixed-width column.
  // Its editor likewise: a one-line value, edited in a box that wraps it onto as many lines as
  // it takes and is as tall as they are, so all of it shows however wide its letters are; the
  // box keeps about eleven characters' width (or the whole width, when that is less), and the
  // save and cancel buttons move below it when there is no room for them beside it.
  keepWordsWhole = false,
  background,
  color,
  textClicked,
  sensitive
}) {
  const [editMode, setEditMode] = useState(mode === 'edit');
  const [value, setValue] = useState(initialValue);
  const [originalValue, setOriginalValue] = useState(initialValue); // Keep track of the original value
  const [isSaving, setIsSaving] = useState(false); // New state to track saving status
  const toast = useToast();
  const theme = useTheme();
  const iconBackgroundColor = background || useColorModeValue( '#F1F1F5', '#303343');
  const iconColor = color || useColorModeValue( theme.colors.iconColor, '#F9F9FB');

  const handleSave = async () => {
    if (pattern && !pattern.test((value || '').trim())){
      if (invalid){
        toast({
          title: "Not a valid value",
          description: invalid,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }  
      return;
    }
    setIsSaving(true); // Indicate saving is in progress
    const saveResult = await onSave(value.trim());
    setIsSaving(false); // Reset saving indicator
    if (saveResult) {
      setOriginalValue(value.trim());
    } else {
      if (showValidationToast) {
        toast({
          title: "Not a valid value",
          description: invalid,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      setValue(originalValue);
    }
    setEditMode(false);
  };

  useEffect(() => {
    // Update state when initialValue prop changes
    setValue(initialValue);
    setOriginalValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    setEditMode(mode === 'edit');
    onModeChange(mode === 'edit');
  }, [mode]);

  useEffect(() => {
    onModeChange(editMode);
  }, [editMode]);

  // keepWordsWhole: the editor's box is as tall as the lines it wraps the value onto, fitted
  // again as the value changes, whenever the box's width does (a resized window), and when the
  // page's fonts finish loading (one swapped in after editing started wraps it anew)
  const editorRef = useRef(null);
  const fitEditor = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const { borderTopWidth, borderBottomWidth } = window.getComputedStyle(editor);
    editor.style.height = "auto";
    editor.style.height = `${editor.scrollHeight + (parseFloat(borderTopWidth) || 0) + (parseFloat(borderBottomWidth) || 0)}px`;
  };
  useLayoutEffect(() => {
    if (keepWordsWhole && editMode) fitEditor();
  }, [keepWordsWhole, editMode, value]);
  useEffect(() => {
    const editor = editorRef.current;
    if (!keepWordsWhole || !editMode || !editor) return undefined;
    const fonts = document.fonts;
    fonts?.addEventListener?.("loadingdone", fitEditor);
    let observer;
    let frame;
    if (typeof ResizeObserver !== "undefined") {
      let width = editor.clientWidth;
      observer = new ResizeObserver(() => {
        if (editor.clientWidth === width) return; // its height, just fitted
        width = editor.clientWidth;
        // on the next frame, as resizing the box it observes from here would loop
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(fitEditor);
      });
      observer.observe(editor);
    }
    return () => {
      fonts?.removeEventListener?.("loadingdone", fitEditor);
      observer?.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [keepWordsWhole, editMode]);

  // keepWordsWhole: the value is one line, as in an input: a line break pasted or dropped into
  // it is a space, the caret staying where it was (the box's own value is set here, so React has
  // no different one to write back, which would move the caret to the end)
  const handleChange = (e) => {
    const box = e.target;
    if (keepWordsWhole && /[\r\n]/.test(box.value)) {
      const oneLine = (text) => text.replace(/\r\n|[\r\n]/g, " ");
      const [start, end] = [box.selectionStart, box.selectionEnd]
        .map((offset) => oneLine(box.value.slice(0, offset)).length);
      box.value = oneLine(box.value);
      box.setSelectionRange(start, end);
    }
    setValue(box.value);
  };

  const handleCancel = async () => {
    setValue(originalValue);
    await onCancel();
    setEditMode(false);
  };

  /**
   * Conditionally return placeholder depending on showPlaceholderInDisplayMode
   * @returns {string}
   */
  const getPlaceholder = () => {
    if (!showPlaceholderInDisplayMode && !editMode) {
      return '';
    }

    return placeholder;
  };

  const EditComponent = multiline || keepWordsWhole ? Textarea : Input;

  const displayText = (
    <Text
      as="span"
      flexGrow={1}
      onClick={() => editable && !isSaving && (textClicked? textClicked() : setEditMode(true)) }
      textAlign="left"
      {...(keepWordsWhole
        ? { wordBreak: "normal", overflowWrap: "normal", overflow: "hidden", textOverflow: "ellipsis" }
        : { wordBreak: "break-word" })}
      isTruncated={truncate}
    >
      {!sensitive ? (value !== undefined && value !== null ? value : getPlaceholder()) : '*****'}
    </Text>
  );
  const editButton = editable && !isSaving && (
    <IconButton
      float="right"
      id={"edit-text"}
      icon={<EditIcon />}
      onClick={() => setEditMode(true)}
      size="sm"
      // keepWordsWhole: at the right end of its line, below the text or beside it alike
      ml={keepWordsWhole ? "auto" : 2}
      aria-label="Edit"
      bg={iconBackgroundColor}
      color={iconColor}
    />
  );

  const editInput = (
    <EditComponent
      as={EditComponent} // Conditionally set the component
      value={value}
      onChange={handleChange}
      placeholder={getPlaceholder()}
      size="sm"
      {...(keepWordsWhole
        ? {
          ref: editorRef,
          // the value wraps; the box is as tall as its lines (fitEditor), one line as tall as
          // an input (20px, 5px above and below, and the border)
          rows: 1,
          resize: "none",
          overflow: "hidden",
          minH: 0,
          lineHeight: "20px",
          py: "5px",
          // Enter adds no line break, as in an input
          onKeyDown: (e) => e.key === "Enter" && !e.nativeEvent.isComposing && e.preventDefault(),
          // eleven characters of its text, with its padding (0.5rem a side, a little less than
          // elsewhere, for more of a name in a narrow column) and border; it grows to the rest
          // of its line and, minW 0, shrinks to a width narrower than that
          flex: "1 1 calc(11ch + 1rem + 2px)",
          minW: 0,
          px: 2,
        }
        : { width: "auto", flexGrow: 1 })}
      isDisabled={isSaving} // Disable input while saving
    />
  );
  const editButtons = isSaving ? (
    <Spinner size="sm" ml={2} />
  ) : (
    <>
      <IconButton
        id={"save-edit-text"}
        icon={<CheckIcon />}
        onClick={handleSave}
        size="sm"
        ml={2}
        aria-label="Save"
        isDisabled={isSaving}
        bg={iconBackgroundColor}
        color={iconColor}
      />
      <IconButton
        id={"cancel-edit-text"}
        icon={<CloseIcon />}
        onClick={handleCancel}
        size="sm"
        ml={2}
        aria-label="Cancel"
        isDisabled={isSaving}
        bg={iconBackgroundColor}
        color={iconColor}
      />
    </>
  );

  return (
    <>
      {editMode ? (
        keepWordsWhole ? (
          // the buttons, below the input when not beside it, at the right end of their line
          <Flex flexWrap="wrap" align="center" justify="flex-end" rowGap={2} flexGrow={1} minW={0}>
            {editInput}
            {/* one item, so the two move below together */}
            <Flex align="center">{editButtons}</Flex>
          </Flex>
        ) : (
          <>
            {editInput}
            {editButtons}
          </>
        )
      ) : keepWordsWhole ? (
        // the gap is only between the two while they share a line
        <Flex flexWrap="wrap" align="center" columnGap={2} flexGrow={1} minW={0}>
          {displayText}
          {editButton}
        </Flex>
      ) : (
        <>
          {displayText}
          {editButton}
        </>
      )}
    </>
  );
}

export default EditableText;
